<script setup lang="ts">
import type { Chart } from 'highcharts';
import { computed, ref, toRef } from 'vue';
import { RotateCcw } from 'lucide-vue-next';
import GenericChart from '@/components/charts/GenericChart.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { i18n } from '@/plugins/internationalization/i18n';
import type { SlaughterWeekBin } from '@/composables/records/useSlaughtersChartOptions';
import { useSlaughtersChartOptions } from '@/composables/records/useSlaughtersChartOptions';
import { clearHoverBand } from '@/composables/records/useRecordsChartOptions';

/*
 * Slaughters' own fork of `RecordsWeeklyChart`/`GroupedWeeklyChart`: one grouped pair of bars
 * per week - pig count always, discarded pig count only once the checkbox below is checked, on its
 * own y-axis. Click-to-select-a-week, fade-on-selection and the hover band behave the same as every
 * other records chart and are resolved the same way, in `useSlaughtersChartOptions`.
 */
const props = defineProps<{
    bins: SlaughterWeekBin[];
    selectedWeekStart: string | null;
    /** Hex of the main series' colour, from the `chart.slaughters` token. */
    pigsColor: string;
    /** Hex of the discarded series' colour, from `chart.slaughtersDiscarded`. */
    discardedColor: string;
    title: string;
}>();

const emit = defineEmits<{
    'week-selected': [weekStart: string];
    'selection-reset': [];
}>();

// Off until the reader asks for it: discarded pigs are the smaller, secondary figure, and drawing
// them by default would compete with the pig-count bars this chart is opened to read first.
const showDiscarded = ref(false);

const pigsSeriesName = computed(() => i18n.global.t('views.pigs.records.chart.seriesName'));
const discardedSeriesName = computed(() => i18n.global.t('views.pigs.records.table.headings.discardedPigs'));

const options = useSlaughtersChartOptions(toRef(props, 'bins'), toRef(props, 'selectedWeekStart'), {
    pigsColor: toRef(props, 'pigsColor'),
    discardedColor: toRef(props, 'discardedColor'),
    title: toRef(props, 'title'),
    pigsSeriesName,
    discardedSeriesName,
    showDiscarded,
    onBinClicked: (weekStart: string) => emit('week-selected', weekStart),
});

function onChartUpdate(instance: Chart) {
    // A redraw drops imperatively added bands; the next pointer move puts a fresh one back.
    clearHoverBand(instance);
}

function onDiscardedToggle(checked: boolean | 'indeterminate') {
    showDiscarded.value = checked === true;
}
</script>

<template>
    <!-- GenericChart declares `options` as a ComputedRef, which the template unwraps before it is
         checked; every chart in the app casts here for the same reason. -->
    <GenericChart :options="(options as any)" @update="onChartUpdate">
        <template #prepend-actions>
            <label class="flex items-center gap-1.5 mr-1 text-xs text-gray-600 cursor-pointer select-none">
                <Checkbox :checked="showDiscarded" @update:checked="onDiscardedToggle" />
                {{ $t('views.pigs.slaughters.chart.showDiscarded') }}
            </label>
            <Button variant="outline" size="sm" :disabled="selectedWeekStart === null" :aria-label="$t('views.pigs.records.chart.resetSelectionHint')" @click="emit('selection-reset')">
                <RotateCcw class="size-4 mr-1.5" aria-hidden="true" />
                {{ $t('views.pigs.records.chart.resetSelection') }}
            </Button>
        </template>
    </GenericChart>
</template>
